import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { handleError } from '../../lib/errorHandler';
import api, { leadsAPI, invoicesAPI, hrAPI, scheduleAPI, dealsAPI } from '../../lib/api';
import { Confetti } from '../../components/ui/confetti';

import {
  TrendingUp, DollarSign, IndianRupee, Users, Briefcase,
  AlertCircle, RefreshCw, PanelRightOpen, X, Sparkles, ChevronRight,
  Building2, Search, Home, BarChart3, UserCheck,
  Crown, Rocket, ClipboardList, Eye,
  Brain, CheckCircle2, Layers, Radio, Clock, Upload, Settings2, Mail, UserPlus,
  Activity, Award, Target, ArrowRight
} from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useAIState } from '../../contexts/AIStateContext';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, PieChart, Pie, Cell,
} from 'recharts';

import BrainMorningBriefing from '../../components/dashboard/BrainMorningBriefing';
import PowerUpBrainCard from '../../components/dashboard/PowerUpBrainCard';
import BrainChatHero from '../../components/dashboard/BrainChatHero';
import AttentionCard from '../../components/dashboard/AttentionCard';
import LeoHandledTodayCard from '../../components/dashboard/LeoHandledTodayCard';
import BrainActivityFeed from '../../components/dashboard/BrainActivityFeed';
import AutonomyInsightsCard from '../../components/dashboard/AutonomyInsightsCard';
import DeliverabilityCard from '../../components/dashboard/DeliverabilityCard';
import BrainSuggestions from '../../components/dashboard/BrainSuggestions';
import FirstHourChecklistCard from '../../components/dashboard/FirstHourChecklistCard';
import SetupProgressWidget from '../../components/dashboard/SetupProgressWidget';
import HotRepliesWidget from '../../components/dashboard/HotRepliesWidget';
import OnboardingFallbackBanner from '../../components/dashboard/OnboardingFallbackBanner';
import { AutonomyTicker } from '../../components/dashboard/AutonomyTicker';

// BrainChatHero serves as the integrated command bar

/* ── Phase 3: Cadence Opt-in Banner — shown after ICP+leads land but before cadence configured ── */
function CadenceOptInBanner() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('cadence_optin_dismissed') === 'true') return;
    let cancelled = false;
    (async () => {
      try {
        // Iter 60 #7 — hide banner if cadence_configured already true OR if any
        // tenant cadence already exists (defensive: in case the brain ran
        // autonomously and the flag wasn't flipped). Two-source check stops us
        // from prompting "Set up outreach" when a sequence is already running.
        const [statusRes, cadencesRes] = await Promise.allSettled([
          api.get('/api/onboarding/status'),
          api.get('/api/brain/cadences').catch(() => ({ data: { cadences: [] } })),
        ]);
        if (cancelled) return;
        const d = (statusRes.status === 'fulfilled' && statusRes.value.data) || {};
        const cadences = (cadencesRes.status === 'fulfilled' && cadencesRes.value.data?.cadences) || [];
        const hasActiveCadence = Array.isArray(cadences) && cadences.some(
          (c) => !c.lead_id && (c.is_active !== false)
        );
        if (d.brain_data_seeded && !d.cadence_configured && !hasActiveCadence) {
          setShow(true);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSetup = async () => {
    setBusy(true);
    try {
      await api.post('/api/onboarding/generate-cadence');
      setDone(true);
      setTimeout(() => setShow(false), 2500);
    } catch (e) {
      setBusy(false);
      console.warn('cadence opt-in failed', e);
    }
  };

  const handleDismiss = () => {
    try { localStorage.setItem('cadence_optin_dismissed', 'true'); } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <Card className="mb-6 border-violet-500/30 bg-gradient-to-r from-violet-500/5 to-blue-500/5" data-testid="cadence-optin-banner">
      <CardContent className="p-5 flex items-center gap-4">
        <div className="w-11 h-11 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
          <Rocket className="h-5 w-5 text-violet-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-foreground">
            {done ? "Your cadence is being designed…" : "Ready to reach out?"}
          </div>
          <div className="text-sm text-muted-foreground">
            {done
              ? "Brain is building a 6-step outreach sequence tailored to your ICP. Check Cadences in a minute."
              : "Your 3 AI leads are ready. Design a tailored outreach cadence and we'll enroll them automatically."}
          </div>
        </div>
        {!done && (
          <>
            <Button onClick={handleSetup} disabled={busy} data-testid="cadence-optin-setup">
              {busy ? 'Starting…' : 'Set up outreach'}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleDismiss} data-testid="cadence-optin-dismiss">
              <X className="h-4 w-4" />
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}


/* ── Phase A: Enhanced AI Setup Banner — sticky top, shows live step list from
       /api/onboarding/setup/status/{job_id}. Same data the wizard used to render. ── */
function AISetupBanner({ onReady }) {
  const [progress, setProgress] = useState(5);
  const [steps, setSteps] = useState([]);
  const [icpDone, setIcpDone] = useState(false);
  const pollRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    const poll = async () => {
      try {
        const jobId = localStorage.getItem('onboarding_job_id');
        if (jobId) {
          const jobRes = await api.get(`/api/onboarding/setup/status/${jobId}`);
          const jd = jobRes.data || {};
          const job = jd.job || jd;
          const jobSteps = Array.isArray(job.steps) ? job.steps : [];
          if (jobSteps.length) setSteps(jobSteps);
          if (job.progress) setProgress(Math.min(job.progress, job.status === 'complete' ? 100 : 95));

          const generateIcpStep = jobSteps.find(s => s.key === 'generate_icp');
          if (generateIcpStep?.status === 'done') setIcpDone(true);

          if (job.status === 'complete' || job.status === 'partial') {
            localStorage.removeItem('onboarding_job_id');
            setProgress(100);
            setTimeout(() => onReady(), 800);
            clearInterval(pollRef.current);
            return;
          }
        }
        // Fallback: also check tenant flag in case job_id was lost
        const statusRes = await api.get('/api/onboarding/status');
        const sd = statusRes.data || {};
        if (sd.brain_data_seeded) {
          setProgress(100);
          localStorage.removeItem('onboarding_job_id');
          setTimeout(() => onReady(), 800);
          clearInterval(pollRef.current);
          return;
        }
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        const simulated = Math.min(Math.round(10 + (elapsed / 60) * 80), 92);
        setProgress(prev => Math.max(prev, simulated));
      } catch { /* silent — keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => clearInterval(pollRef.current);
  }, [onReady]);

  const isDone = progress >= 100;

  // Filter out hidden background steps (cadence, demo_only fallback)
  const visibleSteps = (steps || []).filter(
    (s) => !s.hidden && (s.key !== 'demo_only' || s.status !== 'pending')
  );

  return (
    <div
      className="mt-6 rounded-xl border border-blue-500/20 bg-gradient-to-r from-blue-500/[0.06] to-violet-500/[0.04] p-4 sm:p-5 animate-[fadeUp_0.4s_ease-out]"
      data-testid="ai-setup-banner"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
          {isDone
            ? <CheckCircle2 className="h-4 w-4 text-green-500" />
            : <Brain className="h-4 w-4 text-blue-500 animate-pulse" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-semibold truncate">
              {isDone ? 'AI Setup Complete' : icpDone ? 'Finding your leads…' : 'AI is studying your business'}
            </p>
            <span className="text-[11px] font-mono font-semibold text-blue-600 dark:text-blue-400 shrink-0" data-testid="ai-setup-progress-pct">
              {Math.round(progress)}%
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {isDone
              ? 'Your workspace is ready — leads landing on your Kanban now.'
              : icpDone
                ? 'ICP built. Matching prospects to your ideal buyer — they\'ll appear in seconds.'
                : 'Reading your website, classifying your industry, building your ICP…'}
          </p>
        </div>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden bg-muted/40 mb-3">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{ width: `${Math.max(progress, 5)}%`, background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #06b6d4)' }}
          data-testid="ai-setup-progress-bar"
        />
      </div>
      {visibleSteps.length > 0 && (
        <ul className="space-y-1.5 mt-2" data-testid="ai-setup-step-list">
          {visibleSteps.map((s) => {
            const icon = s.status === 'done' ? '✓'
              : s.status === 'failed' ? '✗'
              : s.status === 'processing' ? '◐'
              : s.status === 'skipped' ? '–'
              : '○';
            const cls =
              s.status === 'done' ? 'text-emerald-600 dark:text-emerald-400'
              : s.status === 'failed' ? 'text-amber-600 dark:text-amber-400'
              : s.status === 'processing' ? 'text-blue-600 dark:text-blue-400'
              : s.status === 'skipped' ? 'text-muted-foreground/60'
              : 'text-muted-foreground/50';
            return (
              <li key={s.key} className={`flex items-start gap-2 text-xs ${cls}`} data-testid={`ai-setup-step-${s.key}`}>
                <span className={`font-mono font-bold w-3 ${s.status === 'processing' ? 'animate-spin' : ''}`}>{icon}</span>
                <div className="flex-1 min-w-0">
                  <span className={`font-medium ${s.status === 'processing' ? 'animate-pulse' : ''}`}>{s.label}</span>
                  {s.result ? <span className="ml-2 text-muted-foreground/80 truncate">— {s.result}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/* ── AI Suggestion Widget ── */
function AISuggestionWidget() {
  const navigate = useNavigate();
  // Iter 52: dynamic personalized pills from the user's actual ICP (was hardcoded
  // generic Director/C-Suite/Founder/VP/Manager/SaaS list — felt static and not
  // "wow"). Falls back to the generic list while the ICP is still building.
  const [icpPills, setIcpPills] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/icp/');
        if (cancelled) return;
        const icp = res?.data?.icp;
        if (!icp) return;
        const titles = (icp.ideal_lead_titles || []).slice(0, 3);
        const industries = (icp.firmographic?.industries || []).slice(0, 2);
        const region = (icp.firmographic?.geographies || [])[0];
        // Build pills: each title routes to discover by job-title; industries route
        // by industry; region routes by geography.
        const palette = [
          { color: 'text-emerald-500', bg: 'bg-emerald-500/8 hover:bg-emerald-500/14', borderHover: 'hover:border-emerald-300/50 dark:hover:border-emerald-600/40', icon: Crown },
          { color: 'text-cyan-500', bg: 'bg-cyan-500/8 hover:bg-cyan-500/14', borderHover: 'hover:border-cyan-300/50 dark:hover:border-cyan-600/40', icon: UserCheck },
          { color: 'text-rose-400', bg: 'bg-rose-400/8 hover:bg-rose-400/14', borderHover: 'hover:border-rose-300/50 dark:hover:border-rose-600/40', icon: Rocket },
          { color: 'text-sky-500', bg: 'bg-sky-500/8 hover:bg-sky-500/14', borderHover: 'hover:border-sky-300/50 dark:hover:border-sky-600/40', icon: TrendingUp },
          { color: 'text-amber-500', bg: 'bg-amber-500/8 hover:bg-amber-500/14', borderHover: 'hover:border-amber-300/50 dark:hover:border-amber-600/40', icon: Building2 },
          { color: 'text-violet-400', bg: 'bg-violet-400/8 hover:bg-violet-400/14', borderHover: 'hover:border-violet-300/50 dark:hover:border-violet-500/40', icon: Briefcase },
        ];
        const pills = [];
        titles.forEach((t, i) => {
          const p = palette[i % palette.length];
          // Persona chips carry industry+region too so Discover auto-search has
          // real company filters (Apollo can't search companies by job title).
          const extra = `${industries[0] ? `&industry=${encodeURIComponent(industries[0])}` : ''}${region ? `&location=${encodeURIComponent(region)}` : ''}`;
          pills.push({
            label: t.length > 18 ? t.slice(0, 18) + '…' : t,
            description: 'Your ideal buyer',
            route: `/discover-leads?title=${encodeURIComponent(t)}${extra}`,
            ...p,
          });
        });
        industries.forEach((ind, i) => {
          const p = palette[(titles.length + i) % palette.length];
          pills.push({
            label: ind.length > 18 ? ind.slice(0, 18) + '…' : ind,
            description: 'Your target industry',
            route: `/discover-leads?industry=${encodeURIComponent(ind)}`,
            ...p,
          });
        });
        if (region) {
          const p = palette[(titles.length + industries.length) % palette.length];
          pills.push({
            label: region.length > 18 ? region.slice(0, 18) + '…' : region,
            description: 'Your sales region',
            route: `/discover-leads?location=${encodeURIComponent(region)}`,
            ...p,
          });
        }
        if (pills.length > 0) setIcpPills(pills.slice(0, 6));
      } catch {
        // ignore — pills will fall back to generic
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fallbackSuggestions = [
    { label: 'Directors', icon: Crown, description: 'Decision-makers', route: '/discover-leads?seniority=director', color: 'text-emerald-500', bg: 'bg-emerald-500/8 hover:bg-emerald-500/14', borderHover: 'hover:border-emerald-300/50 dark:hover:border-emerald-600/40' },
    { label: 'C-Suite', icon: UserCheck, description: 'Top executives', route: '/discover-leads?seniority=c_suite', color: 'text-cyan-500', bg: 'bg-cyan-500/8 hover:bg-cyan-500/14', borderHover: 'hover:border-cyan-300/50 dark:hover:border-cyan-600/40' },
    { label: 'Founders', icon: Rocket, description: 'Startup founders', route: '/discover-leads?seniority=founder', color: 'text-rose-400', bg: 'bg-rose-400/8 hover:bg-rose-400/14', borderHover: 'hover:border-rose-300/50 dark:hover:border-rose-600/40' },
    { label: 'VP', icon: TrendingUp, description: 'Vice presidents', route: '/discover-leads?seniority=vp', color: 'text-sky-500', bg: 'bg-sky-500/8 hover:bg-sky-500/14', borderHover: 'hover:border-sky-300/50 dark:hover:border-sky-600/40' },
    { label: 'Managers', icon: ClipboardList, description: 'Team managers', route: '/discover-leads?seniority=manager', color: 'text-amber-500', bg: 'bg-amber-500/8 hover:bg-amber-500/14', borderHover: 'hover:border-amber-300/50 dark:hover:border-amber-600/40' },
    { label: 'SaaS Companies', icon: Building2, description: 'Software industry', route: '/discover-leads?industry=computer+software', color: 'text-violet-400', bg: 'bg-violet-400/8 hover:bg-violet-400/14', borderHover: 'hover:border-violet-300/50 dark:hover:border-violet-500/40' },
  ];
  const suggestions = icpPills && icpPills.length > 0 ? icpPills : fallbackSuggestions;

  return (
    <div className="mt-8 animate-[fadeUp_0.5s_ease-out_0.2s_both]" data-testid="ai-suggestion-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-cyan-500" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            {icpPills && icpPills.length > 0 ? 'Your AI ICP — Top Matches' : 'AI ICP Suggestions'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2.5 text-[11px] text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/discover-leads')}
          data-testid="ai-suggestions-view-all"
        >
          <Eye className="h-3 w-3 mr-1" />
          View All
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {suggestions.map((s) => {
          const Icon = s.icon;
          return (
            <button
              key={s.label}
              onClick={() => navigate(s.route)}
              className={`group flex flex-col items-start gap-2 p-3.5 rounded-xl border border-border/60 ${s.bg} ${s.borderHover} transition-all duration-200 text-left`}
              data-testid={`ai-suggestion-${s.label.toLowerCase().replace(/[\s-]+/g, '-')}`}
            >
              <div className="flex items-center gap-2 w-full">
                <Icon className={`h-4 w-4 ${s.color} shrink-0`} />
                <span className="text-xs font-semibold text-foreground/80 group-hover:text-foreground truncate">{s.label}</span>
              </div>
              <span className="text-[10px] text-muted-foreground/70 leading-tight">{s.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Phase A: Magic Moment Modal — pulled in from the wizard.
   Fires on the dashboard the instant brain_data_seeded flips, so the user
   gets the celebratory reveal in the workspace they're already exploring. ── */
function MagicMomentModal({ onDismiss }) {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/onboarding/status');
        if (!cancelled) {
          const sd = res.data || {};
          setSummary({
            company_name: sd.company_name || '',
            industry: sd.industry || '',
            leads_count: sd.lead_count || sd.total_leads || 0,
            kb_pages: sd.kb_pages || 0,
            cadence_configured: !!sd.cadence_configured,
          });
        }
      } catch {
        if (!cancelled) setSummary({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!summary) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-[fadeIn_0.3s_ease-out]"
      style={{ backdropFilter: 'blur(10px)', background: 'rgba(0,0,0,0.55)' }}
      data-testid="magic-moment-modal"
    >
      <div className="max-w-md w-full rounded-3xl p-8 shadow-2xl animate-[popIn_0.4s_cubic-bezier(0.2,0.8,0.2,1)] border bg-card/95 border-primary/30">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-blue-500 shadow-xl shadow-primary/30 mb-4">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-1">Your AI CRM is ready</h2>
          <p className="text-sm text-muted-foreground">Everything was set up while you explored</p>
        </div>

        <div className="space-y-3 mb-6 rounded-2xl p-4 bg-muted/30">
          {summary.leads_count > 0 && (
            <div className="flex items-center gap-3" data-testid="magic-leads">
              <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                <Users className="h-4 w-4 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{summary.leads_count} lead{summary.leads_count !== 1 ? 's' : ''} in your pipeline</div>
                <div className="text-xs text-muted-foreground">Matched to your ICP, ready to engage</div>
              </div>
            </div>
          )}
          {summary.industry && (
            <div className="flex items-center gap-3" data-testid="magic-industry">
              <div className="w-9 h-9 rounded-xl bg-blue-500/15 flex items-center justify-center shrink-0">
                <Building2 className="h-4 w-4 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{summary.industry}</div>
                <div className="text-xs text-muted-foreground">Industry detected & ICP built</div>
              </div>
            </div>
          )}
          {summary.kb_pages > 0 && (
            <div className="flex items-center gap-3" data-testid="magic-kb">
              <div className="w-9 h-9 rounded-xl bg-amber-500/15 flex items-center justify-center shrink-0">
                <Sparkles className="h-4 w-4 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-foreground">{summary.kb_pages} pages saved to knowledge base</div>
                <div className="text-xs text-muted-foreground">Brain uses these for every decision</div>
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={onDismiss}
          className="w-full h-11 rounded-xl bg-gradient-to-r from-primary to-blue-500 hover:from-primary/90 hover:to-blue-500/90 text-white font-semibold shadow-lg shadow-primary/20"
          data-testid="magic-enter-btn"
        >
          Explore my CRM <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

/* ── Phase 6: Manual Pro (no-AI) dashboard — ENTERPRISE COMMAND CENTER.
   High-density KPI row + pipeline stage list (inline progress) + source donut +
   premium setup console. Rendered on the Home tab only when planAiEnabled===false.
   No AI surfaces. Design per /app/design_guidelines.json. ── */
const CHART_COLORS = ['#4F46E5', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#3B82F6'];
const FONT_HEAD = "'Manrope', ui-sans-serif, system-ui, sans-serif";
const FONT_BODY = "'IBM Plex Sans', ui-sans-serif, system-ui, sans-serif";
const WON_RE = /won|payment received|convert to client/i;
const LOST_RE = /lost|reject|archiv|not interested|not response|charges issue|done with other/i;

/* Measures its own width via ResizeObserver and renders the recharts chart with
   an explicit pixel width/height only once the container has a real size —
   fully avoids recharts width(-1)/height(-1) warnings from ResponsiveContainer. */
function MeasuredChart({ height, className, render }) {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width || 0;
      if (w > 0) setWidth(Math.floor(w));
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{ height }}>
      {width > 0 ? render(width, height) : null}
    </div>
  );
}

function KpiCard({ title, value, sub, icon: Icon, accent, sparkData, badge, delay, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative col-span-1 lg:col-span-3 rounded-xl border border-border/60 bg-card overflow-hidden group"
      data-testid={testid}
    >
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground" style={{ fontFamily: FONT_HEAD }}>{title}</span>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${accent}1a` }}>
            <Icon className="h-4 w-4" strokeWidth={1.75} style={{ color: accent }} />
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="text-3xl sm:text-4xl font-medium tracking-tighter text-foreground" style={{ fontFamily: FONT_HEAD }} data-testid={`${testid}-value`}>{value}</div>
            <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
          </div>
          {badge}
        </div>
      </div>
      {sparkData && sparkData.length > 1 && (
        <MeasuredChart
          height={36}
          className="w-full opacity-70 group-hover:opacity-100 transition-opacity duration-300"
          render={(w, h) => (
            <AreaChart width={w} height={h} data={sparkData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={`spark-${testid}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="v" stroke={accent} strokeWidth={1.5} fill={`url(#spark-${testid})`} />
            </AreaChart>
          )}
        />
      )}
    </motion.div>
  );
}

function ManualProDashboard() {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [total, setTotal] = useState(0);
  const [byStage, setByStage] = useState([]);
  const [bySource, setBySource] = useState([]);
  const [dealSummary, setDealSummary] = useState(null);
  const [followupsDue, setFollowupsDue] = useState(0);
  const [followupsOverdue, setFollowupsOverdue] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bdRes, fuRes, dsRes] = await Promise.allSettled([
          leadsAPI.getBreakdown(),
          scheduleAPI.getFollowups({ status: 'pending' }),
          dealsAPI.getSummary(),
        ]);
        if (cancelled) return;
        if (bdRes.status === 'fulfilled') {
          const d = bdRes.value.data || {};
          setTotal(d.total || 0);
          setByStage(Array.isArray(d.by_stage) ? d.by_stage : []);
          setBySource(Array.isArray(d.by_source) ? d.by_source : []);
        }
        if (dsRes.status === 'fulfilled') setDealSummary(dsRes.value.data || null);
        const fus = fuRes.status === 'fulfilled' ? (fuRes.value.data?.followups || []) : [];
        const todayStr = new Date().toISOString().slice(0, 10);
        let overdue = 0;
        fus.forEach((f) => {
          const dd = (f.due_date || '').slice(0, 10);
          if (dd && dd < todayStr) overdue += 1;
        });
        setFollowupsDue(fus.length);
        setFollowupsOverdue(overdue);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const fmt = (n) => (n || 0).toLocaleString();
  const wonCount = byStage.filter((s) => WON_RE.test(s.label)).reduce((a, s) => a + s.count, 0);
  const lostCount = byStage.filter((s) => LOST_RE.test(s.label)).reduce((a, s) => a + s.count, 0);
  const activePipeline = Math.max(total - wonCount - lostCount, 0);
  const conversion = total > 0 ? ((wonCount / total) * 100).toFixed(1) : '0.0';
  const activePct = total > 0 ? Math.round((activePipeline / total) * 100) : 0;
  const stageSpark = byStage.slice(0, 8).map((s) => ({ v: s.count }));
  const maxStage = byStage.length ? Math.max(...byStage.map((s) => s.count)) : 1;

  // Money metrics from deals summary (count + ₹ amount per stage)
  // A14: dashboard is in the workspace currency; only count deals in that currency.
  const dealStages = Array.isArray(dealSummary?.by_stage) ? dealSummary.by_stage : [];
  const stageAmt = (s) => {
    if (Array.isArray(s.by_currency)) {
      const row = s.by_currency.find((c) => c.currency === currency.currencyCode);
      return row ? row.amount : 0;
    }
    return s.amount || 0;
  };
  const wonRevenue = dealStages.filter((s) => WON_RE.test(s.stage || '')).reduce((a, s) => a + stageAmt(s), 0);
  const pipelineValue = dealStages
    .filter((s) => !WON_RE.test(s.stage || '') && !LOST_RE.test(s.stage || ''))
    .reduce((a, s) => a + stageAmt(s), 0);
  const money = (v) => currency.format(v || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  // Source donut: top 5 + "Other"
  const topSources = bySource.slice(0, 5);
  const otherSum = bySource.slice(5).reduce((a, s) => a + s.count, 0);
  const donutData = otherSum > 0 ? [...topSources, { label: 'Other', count: otherSum }] : topSources;

  const checklist = [
    { key: 'import', label: 'Import leads', desc: 'Bring in contacts from CSV or Excel', icon: Upload, route: '/data-import', done: total > 0 },
    { key: 'pipeline', label: 'Set up pipeline', desc: 'Rename & reorder your deal stages', icon: Settings2, route: '/leads-settings', done: byStage.length > 1 },
    { key: 'team', label: 'Invite your team', desc: 'Add teammates to your workspace', icon: UserPlus, route: '/team', done: false },
    { key: 'email', label: 'Connect email', desc: 'Send & log outreach in Lockene', icon: Mail, route: '/integrations', done: false },
  ];

  return (
    <div className="space-y-6" style={{ fontFamily: FONT_BODY }} data-testid="manual-pro-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground" style={{ fontFamily: FONT_HEAD }} data-testid="pro-dashboard-title">Sales Overview</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Your live pipeline at a glance — {loading ? '…' : `${fmt(total)} leads`} across {byStage.length} stages.</p>
        </div>
        <Button size="sm" className="hidden sm:inline-flex gap-1.5" onClick={() => navigate('/leads')} data-testid="pro-open-pipeline-btn">
          Open pipeline <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Row 1 — KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4">
        <KpiCard testid="pro-kpi-total" title="Total Leads" value={loading ? '—' : fmt(total)} sub="All contacts in your CRM" icon={Users} accent="#4F46E5" sparkData={stageSpark} delay={0} />
        <KpiCard testid="pro-kpi-active" title="Active Pipeline" value={loading ? '—' : fmt(activePipeline)} sub={loading ? '—' : `${money(pipelineValue)} open · ${activePct}% in play`} icon={Activity} accent="#3B82F6" sparkData={stageSpark} delay={0.05} />
        <KpiCard testid="pro-kpi-won" title="Closed Won" value={loading ? '—' : fmt(wonCount)} sub={loading ? '—' : `${money(wonRevenue)} won · ${conversion}% conversion`} icon={Award} accent="#10B981" sparkData={stageSpark} delay={0.1} />
        <KpiCard
          testid="pro-kpi-followups"
          title="Follow-ups Due"
          value={loading ? '—' : fmt(followupsDue)}
          sub={followupsOverdue > 0 ? 'Action needed today' : 'You are all caught up'}
          icon={Clock}
          accent={followupsOverdue > 0 ? '#EF4444' : '#F59E0B'}
          delay={0.15}
          badge={followupsOverdue > 0 ? (
            <span className="rounded-full bg-red-500/10 text-red-500 border border-red-500/20 px-2 py-0.5 text-xs font-bold" data-testid="pro-followups-overdue">{followupsOverdue} overdue</span>
          ) : null}
        />
      </div>

      {/* Row 2 — Pipeline list + Source donut */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Pipeline stages */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}
          className="lg:col-span-8 rounded-xl border border-border/60 bg-card"
          data-testid="pro-card-by-stage"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
            <div className="flex items-center gap-2">
              <Layers className="h-4 w-4 text-indigo-400" strokeWidth={1.75} />
              <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: FONT_HEAD }}>Pipeline by Stage</h2>
            </div>
            <span className="text-xs text-muted-foreground">{byStage.length} stages</span>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            {byStage.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">No stages yet — import leads to populate your pipeline.</div>
            ) : byStage.map((s, i) => {
              const pct = maxStage > 0 ? Math.round((s.count / maxStage) * 100) : 0;
              const share = total > 0 ? ((s.count / total) * 100).toFixed(1) : '0.0';
              return (
                <div key={s.label} className="relative overflow-hidden border-b border-border/30 last:border-0 hover:bg-accent/40 transition-colors" data-testid={`pro-stage-row-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <div className="absolute inset-y-0 left-0 bg-indigo-500/10" style={{ width: `${Math.max(pct, 2)}%` }} />
                  <div className="relative z-10 flex items-center justify-between px-5 py-2.5">
                    <span className="text-sm font-medium text-foreground truncate flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {s.label}
                    </span>
                    <span className="flex items-baseline gap-2 shrink-0 tabular-nums">
                      <span className="text-xs text-muted-foreground">{share}%</span>
                      <span className="text-sm font-semibold text-foreground" style={{ fontFamily: FONT_HEAD }}>{fmt(s.count)}</span>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Source distribution */}
        <motion.div
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}
          className="lg:col-span-4 rounded-xl border border-border/60 bg-card"
          data-testid="pro-card-by-source"
        >
          <div className="flex items-center gap-2 px-5 py-4 border-b border-border/40">
            <Radio className="h-4 w-4 text-emerald-400" strokeWidth={1.75} />
            <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: FONT_HEAD }}>Leads by Source</h2>
          </div>
          <div className="p-5">
            {donutData.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">No source data yet.</div>
            ) : (
              <>
                <div className="relative h-[180px]">
                  <MeasuredChart
                    height={180}
                    className="w-full"
                    render={(w, h) => (
                      <PieChart width={w} height={h}>
                        <Pie data={donutData} dataKey="count" nameKey="label" cx="50%" cy="50%" innerRadius={58} outerRadius={80} paddingAngle={2} stroke="none">
                          {donutData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                      </PieChart>
                    )}
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-medium tracking-tighter text-foreground" style={{ fontFamily: FONT_HEAD }}>{fmt(total)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</span>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  {donutData.map((s, i) => {
                    const share = total > 0 ? Math.round((s.count / total) * 100) : 0;
                    return (
                      <div key={s.label} className="flex items-center justify-between gap-2 text-xs" data-testid={`pro-source-legend-${i}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                          <span className="truncate text-foreground/80">{s.label}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">{fmt(s.count)} · {share}%</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

      {/* Row 3 — Setup console */}
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.25 }} data-testid="pro-setup-checklist">
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-4 w-4 text-foreground/70" strokeWidth={1.75} />
          <h2 className="text-sm font-semibold text-foreground" style={{ fontFamily: FONT_HEAD }}>Get your CRM set up</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {checklist.map((step) => {
            const Icon = step.icon;
            return (
              <div
                key={step.key}
                className={`rounded-xl border p-4 flex flex-col gap-3 transition-colors ${step.done ? 'bg-emerald-500/[0.06] border-emerald-500/20' : 'bg-card border-border/60 hover:border-border'}`}
                data-testid={`pro-checklist-${step.key}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${step.done ? 'bg-emerald-500/15' : 'bg-muted/60'}`}>
                    {step.done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" strokeWidth={2} /> : <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                  </div>
                  {step.done && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-500">Done</span>}
                </div>
                <div>
                  <div className={`text-sm font-semibold ${step.done ? 'text-foreground/70' : 'text-foreground'}`} style={{ fontFamily: FONT_HEAD }}>{step.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{step.desc}</div>
                </div>
                <Button
                  variant={step.done ? 'ghost' : 'default'}
                  size="sm"
                  className="mt-auto w-full"
                  onClick={() => navigate(step.route)}
                  data-testid={`pro-checklist-btn-${step.key}`}
                >
                  {step.done ? 'Manage' : 'Set up'} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
}

export default function OverviewDashboard() {
  const navigate = useNavigate();
  const { planAiEnabled, aiStatusLoaded } = useAIState();
  const isManualPro = aiStatusLoaded && planAiEnabled === false;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [data, setData] = useState({
    sales: { total: 0, value: 0 },
    accounts: { revenue: 0, outstanding: 0 },
    hr: { total: 0, active: 0 },
    lastUpdated: null
  });

  // Workspace preparation state
  const [isPreparingWorkspace, setIsPreparingWorkspace] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showMagicMoment, setShowMagicMoment] = useState(false);
  const workspaceCheckedRef = useRef(false);

  const fetchData = useCallback(async (modules) => {
    setLoading(true);
    setError(null);
    try {
      const enabledModules = modules || [];
      const hasMod = (m) => enabledModules.length === 0 || enabledModules.includes(m);

      const [leadsRes, invoicesRes, hrRes] = await Promise.allSettled([
        hasMod('sales') ? leadsAPI.getAll({ limit: 500 }) : Promise.resolve({ data: [] }),
        hasMod('accounts') ? invoicesAPI.getAll({ limit: 500 }) : Promise.resolve({ data: [] }),
        hrAPI.getUsers({ limit: 500 })
      ]);

      const leads = leadsRes.status === 'fulfilled' ? (leadsRes.value.data?.data || leadsRes.value.data || []) : [];
      const totalValue = leads.reduce((sum, l) => sum + (parseFloat(l.value) || parseFloat(l.expected_value) || 0), 0);

      const invoices = invoicesRes.status === 'fulfilled' ? (invoicesRes.value.data?.data || invoicesRes.value.data || []) : [];
      let revenue = 0, outstanding = 0;
      invoices.forEach(inv => {
        const total = inv.total || inv.amount || 0;
        if (inv.status === 'paid') revenue += total;
        else outstanding += total;
      });

      const employees = hrRes.status === 'fulfilled' ? (hrRes.value.data?.data || hrRes.value.data || []) : [];
      const activeEmployees = employees.filter(e => e.is_active !== false).length;

      setData({
        sales: { total: leads.length, value: totalValue },
        accounts: { revenue, outstanding },
        hr: { total: employees.length, active: activeEmployees },
        lastUpdated: new Date()
      });
    } catch (err) {
      handleError('Dashboard', err, { silent: true });
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Module selection state — must be declared before useEffect that references it
  const [selectedModules, setSelectedModules] = useState(null);
  const [chatActive, setChatActive] = useState(false);

  // When workspace is preparing, still show the dashboard — just skip the early return
  useEffect(() => {
    const init = async () => {
      try {
        const res = await api.get('/api/onboarding/status');
        const mods = res.data?.selected_modules || null;
        setSelectedModules(mods);

        // Check if workspace is still being prepared (post-onboarding, pre-seeding)
        if (!workspaceCheckedRef.current) {
          workspaceCheckedRef.current = true;
          const sd = res.data;
          if (sd && !sd.brain_data_seeded && !sd.has_created_lead && sd.company_name && (sd.onboarding_completed || localStorage.getItem('onboarding_job_id'))) {
            setIsPreparingWorkspace(true);
          }
        }

        fetchData(mods);
      } catch {
        setSelectedModules(null);
        fetchData(null);
      }
    };
    init();
    const handleRefresh = () => fetchData(selectedModules);
    window.addEventListener('intelligence:refresh', handleRefresh);
    return () => window.removeEventListener('intelligence:refresh', handleRefresh);
  }, [fetchData]);

  const handleWorkspaceReady = useCallback(() => {
    setIsPreparingWorkspace(false);
    setShowCelebration(true);
    // Phase A: pull the wizard's "Magic Moment" reveal onto the dashboard.
    // The wizard set onboarding_just_completed=true in localStorage right
    // before redirecting; we surface the modal once the worker is done.
    try {
      if (localStorage.getItem('onboarding_just_completed') === 'true') {
        setShowMagicMoment(true);
      }
    } catch { /* localStorage unavailable */ }
    fetchData(selectedModules);
    setTimeout(() => setShowCelebration(false), 4000);
  }, [fetchData, selectedModules]);

  const handleDismissMagic = useCallback(() => {
    setShowMagicMoment(false);
    try { localStorage.removeItem('onboarding_just_completed'); } catch {}
  }, []);

  const currency = useCurrency();
  const formatCurrency = (value) =>
    currency.format(value || 0, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const MoneyIcon = currency.currencyCode === 'INR' ? IndianRupee : DollarSign;

  if (error) {
    return (
      <div className="p-6 lg:p-8">
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/50">
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-900 dark:text-red-100 mb-2">Unable to Load Dashboard</h3>
            <Button onClick={() => fetchData(selectedModules)} variant="outline"><RefreshCw className="h-4 w-4 mr-2" /> Try Again</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-full" data-testid="overview-dashboard">
      {/* Iter 59b — removed full-screen confetti on OverviewDashboard.
          The Magic Moment modal already provides the celebratory reveal.
          Kept the state so existing callers don't break. */}
      {/* <Confetti active={showCelebration} duration={3000} particleCount={80} /> */}

      {/* Phase A: Magic Moment reveal — fires once when brain_data_seeded flips */}
      {showMagicMoment && <MagicMomentModal onDismiss={handleDismissMagic} />}

      {/* Normal dashboard — always shown. AI setup banner shown inline when preparing */}
      <div className="p-6 lg:p-8">
        {/* Tab Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className={`flex flex-col items-center mb-6 relative transition-all duration-300 ${chatActive ? 'opacity-0 h-0 overflow-hidden mb-0' : ''}`}>
            <TabsList className="h-10 bg-muted/60 backdrop-blur-sm" data-testid="dashboard-tabs">
              <TabsTrigger
                value="home"
                className="gap-1.5 px-5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                data-testid="dashboard-tab-home"
              >
                <Home className="h-3.5 w-3.5" />
                Home
              </TabsTrigger>
              <TabsTrigger
                value="insights"
                className="gap-1.5 px-5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
                data-testid="dashboard-tab-insights"
              >
                <BarChart3 className="h-3.5 w-3.5" />
                Insights
              </TabsTrigger>
            </TabsList>

            {/* Insights panel toggle — only on Insights tab */}
            {activeTab === 'insights' && !panelOpen && (
              <button
                onClick={() => setPanelOpen(true)}
                className="absolute right-0 top-0 p-2 rounded-xl bg-white/60 dark:bg-white/[0.06] border border-border/50 backdrop-blur-sm shadow-sm hover:shadow-md hover:border-cyan-300/40 dark:hover:border-cyan-500/30 transition-all group"
                data-testid="panel-open-btn"
                title="Open insights panel"
              >
                <PanelRightOpen className="h-4 w-4 text-muted-foreground group-hover:text-cyan-500 transition-colors" />
              </button>
            )}
          </div>

          {/* Autonomy proof-of-work ticker (today) — AI surface, hidden for Manual Pro */}
          {!chatActive && !isManualPro && <AutonomyTicker />}

          {/* ═══════════════ HOME TAB ═══════════════ */}
          <TabsContent value="home" className="mt-0 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="dashboard-home-content">
            <div className={isManualPro ? "max-w-[1400px] mx-auto" : "max-w-3xl mx-auto"}>
              {isManualPro ? (
                <ManualProDashboard />
              ) : (
                <>
                  <BrainChatHero hideWelcome onChatActive={setChatActive} />
                  {!chatActive && (
                    <>
                      <OnboardingFallbackBanner />
                      <LeoHandledTodayCard />
                      <div className="mt-4"><AttentionCard /></div>
                      <FirstHourChecklistCard />
                      <SetupProgressWidget className="mt-4" />
                      <div className="mt-6"><HotRepliesWidget /></div>
                      <AISuggestionWidget />
                      {isPreparingWorkspace && <AISetupBanner onReady={handleWorkspaceReady} />}
                      <CadenceOptInBanner />
                      <BrainSuggestions />
                    </>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* ═══════════════ INSIGHTS TAB ═══════════════ */}
          <TabsContent value="insights" className="mt-0 space-y-6 focus-visible:ring-0 focus-visible:ring-offset-0" data-testid="dashboard-insights-content">
            {loading ? (
              <div className="space-y-6" data-testid="insights-skeleton">
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="p-4 rounded-lg border flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-muted/60 animate-pulse shrink-0" />
                      <div className="space-y-2 flex-1">
                        <div className="h-3 w-16 bg-muted/40 rounded animate-pulse" />
                        <div className="h-6 w-20 bg-muted rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-lg border p-6 space-y-3">
                    <div className="h-5 w-32 bg-muted rounded animate-pulse" />
                    <div className="h-48 w-full bg-muted/20 rounded animate-pulse" />
                  </div>
                  <div className="rounded-lg border p-6 space-y-3">
                    <div className="h-5 w-40 bg-muted rounded animate-pulse" />
                    <div className="h-48 w-full bg-muted/20 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ) : (
            <>
            {/* Top Stats Cards */}
            <div className="grid gap-4 grid-cols-2 lg:grid-cols-4" data-testid="live-stats-bar">
              {[
                { label: 'Leads', value: data.sales.total, icon: TrendingUp, color: 'text-sky-600 dark:text-sky-400', bgColor: 'bg-sky-50 dark:bg-sky-950/30' },
                { label: 'Pipeline', value: formatCurrency(data.sales.value), icon: MoneyIcon, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-950/30' },
                { label: 'Revenue', value: formatCurrency(data.accounts.revenue), icon: MoneyIcon, color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-50 dark:bg-emerald-950/30' },
                { label: 'Outstanding', value: formatCurrency(data.accounts.outstanding), icon: AlertCircle, color: data.accounts.outstanding > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground', bgColor: 'bg-amber-50 dark:bg-amber-950/30' },
              ].map((stat, i) => {
                const Icon = stat.icon;
                return (
                  <Card key={i} className="border-border/50" data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${stat.bgColor} flex items-center justify-center shrink-0`}>
                        <Icon className={`h-5 w-5 ${stat.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">{stat.label}</p>
                        <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Three-Column Layout: Briefing | Brain Activity | Power Up */}
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-[280px_1fr_280px]" data-testid="insights-three-col">
              {/* Left: Morning Briefing */}
              <div className="min-w-0">
                <BrainMorningBriefing />
              </div>

              {/* Center: What Your Brain Did */}
              <div className="min-w-0">
                <BrainActivityFeed />
              </div>

              {/* Right: Power Up Your Brain */}
              <div className="min-w-0">
                <PowerUpBrainCard />
              </div>
            </div>

            {/* Autonomy + Deliverability insight cards */}
            <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 mt-6">
              <AutonomyInsightsCard />
              <DeliverabilityCard />
            </div>

            {/* Brain Chat Panel removed — BrainChatHero on Home tab serves as integrated command bar */}
            </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Right Slide Panel — Morning Briefing + Power Up */}
      {panelOpen && (
        <div className="fixed inset-0 z-40" data-testid="insights-panel-overlay">
          <div
            className="absolute inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm animate-[fadeIn_0.15s_ease-out]"
            onClick={() => setPanelOpen(false)}
          />
          <div
            className="absolute top-0 right-0 bottom-0 w-full max-w-md bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl border-l border-white/20 dark:border-white/[0.08] shadow-2xl overflow-y-auto animate-[slideInRight_0.25s_ease-out]"
            data-testid="insights-panel"
          >
            <div className="sticky top-0 z-10 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-900 dark:text-white">Insights & Setup</h2>
              <button
                onClick={() => setPanelOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                data-testid="panel-close-btn"
              >
                <X className="h-4 w-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              <BrainMorningBriefing />
              <PowerUpBrainCard />
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
